// admin-panel/src/pages/ReferralsPage.jsx
import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import {
  Search, Calendar, Phone, UserCheck, IndianRupee, Gift,
  TrendingUp, AlertCircle, CheckCircle2, XCircle, Clock,
  ChevronLeft, ChevronRight, GraduationCap, CreditCard,
  X, Eye, RotateCcw, Sparkles, Send,
  History, MapPin, Mail, RefreshCw, UserPlus
} from 'lucide-react';
import { QRCodeCanvas } from 'qrcode.react';

// =================================================================================
// 1. SHARED HELPERS & SMALL COMPONENTS
// =================================================================================

const formatDateTime = (dateString) => {
  if (!dateString) return 'N/A';
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return 'Invalid Date';
  return date.toLocaleString('en-IN', {
    year: 'numeric', month: 'short', day: '2-digit',
    hour: '2-digit', minute: '2-digit', hour12: true,
  });
};

const toApiDate = (date) => {
  if (!date || isNaN(date.getTime())) return null;
  const offset = date.getTimezoneOffset() * 60000;
  const localISOTime = (new Date(date - offset)).toISOString().slice(0, 10);
  return localISOTime;
};

const Avatar = ({ name }) => {
  const initial = name ? name.trim()[0].toUpperCase() : '?';
  const hashCode = (str) => {
    if (!str) str = '?';
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      hash = str.charCodeAt(i) + ((hash << 5) - hash);
    }
    return hash;
  };
  const colors = [
    { bg: '#667eea', text: '#ffffff' },
    { bg: '#f5576c', text: '#ffffff' },
    { bg: '#4facfe', text: '#ffffff' },
    { bg: '#43e97b', text: '#ffffff' },
  ];
  const color = colors[Math.abs(hashCode(name)) % colors.length];
  return (
    <div className="avatar-circle" style={{ backgroundColor: color.bg, color: color.text }} title={name}>
      {initial}
    </div>
  );
};

const Spinner = () => (
  <svg className="spinner" viewBox="0 0 50 50">
    <circle className="path" cx="25" cy="25" r="20" fill="none" strokeWidth="5"></circle>
  </svg>
);

// =================================================================================
// 2. REFERRAL TRACKER COMPONENT (Embedded)
// =================================================================================

const getNotApplicableSubject = () => {
  const today = new Date().toLocaleDateString('en-IN', { day: '2-digit', month: 'long', year: 'numeric' });
  return `Referral Status Update - ${today}`;
};

const getDefaultNotApplicableBody = (referrerName, referredName, referredMobile) => `Dear ${referrerName || '[Referrer Name]'},

Thank you for referring a learner, **${referredName || '[Learner Name]'} (${referredMobile || '[Contact No.]'})** to Ethan's Tech and for showing your trust and support in our training programs.

We want to inform you that the learner you referred was already registered in our CRM before the referral was submitted through the referral system **(Date of Entry in CRM - [enter date manually])**. As per our referral policy, referrals submitted after a learner's existing registration are not eligible for the Referral Bonus.

However, we sincerely appreciate your effort in referring the learner and your continued support for Ethan's Tech.

Thank you once again for your valuable support and for being a part of our referral program. Keep referring and earn referral bonus.

Thanks & Regards,`;

const ReferralTracker = ({ referral, onClose, onUpdate }) => {
  const [status, setStatus] = useState({
    call_made: false, counselor_assigned: false, walked_in: false, enrolled: false,
    fees_paid: false, reward_sent: false, reward_status: 'Pending',
    reward_type: 'Cash', final_status: 'Pending',
  });

  const [dates, setDates] = useState({});
  const [activityLog, setActivityLog] = useState([]);
  const [newNote, setNewNote] = useState('');
  const [loading, setLoading] = useState(false);

  // --- Local UI Error State ---
  const [uiError, setUiError] = useState('');

  const [showPayoutControls, setShowPayoutControls] = useState(false);
  const [rewardAmount, setRewardAmount] = useState(3000);

  const [showEmailComposer, setShowEmailComposer] = useState(false);
  const [emailSubject, setEmailSubject] = useState('');
  const [emailBody, setEmailBody] = useState('');
  const [sendingEmail, setSendingEmail] = useState(false);
  const [emailError, setEmailError] = useState('');

  const [sendingLead, setSendingLead] = useState(false);

  const historyEndRef = useRef(null);
  const adminRole = localStorage.getItem('admin_role') || 'Counselor';
  const isAdmin = adminRole === 'Admin';
  const isCounselor = adminRole === 'Counselor';

  const progressSteps = [
    { key: 'call_made', label: 'Call Made', icon: Phone, color: '#f59e0b' },
    { key: 'counselor_assigned', label: 'Counselling Done', icon: UserCheck, color: '#8b5cf6' },
    { key: 'walked_in', label: 'Walked-In', icon: MapPin, color: '#ec4899' },
    { key: 'enrolled', label: 'Enrolled', icon: GraduationCap, color: '#667eea' },
    { key: 'fees_paid', label: 'Fees Paid', icon: CreditCard, color: '#10b981' },
  ];

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = 'unset'; };
  }, []);

  // Helper to show UI error and auto-clear it
  const showUiError = (msg) => {
    setUiError(msg);
    setTimeout(() => setUiError(''), 4000); // Clear after 4 seconds
  };

  useEffect(() => {
    if (referral) {
      setStatus({
        call_made: referral.call_made || false,
        counselor_assigned: referral.counselor_assigned || false,
        walked_in: referral.walked_in || false,
        enrolled: referral.enrolled || false,
        fees_paid: referral.fees_paid || false,
        reward_sent: referral.reward_sent || false,
        reward_status: referral.reward_status || 'Pending',
        reward_type: 'Cash',
        final_status: referral.final_status || 'Pending',
      });
      setDates({
        call_made_at: referral.call_made_at,
        counselor_assigned_at: referral.counselor_assigned_at,
        walked_in_at: referral.walked_in_at,
        enrolled_at: referral.enrolled_at,
        fees_paid_at: referral.fees_paid_at,
        reward_sent_at: referral.reward_sent_at,
        admission_done_at: referral.admission_done_at,
        created_at: referral.created_at,
      });

      try {
        if (referral.remarks && referral.remarks.startsWith('[')) {
          setActivityLog(JSON.parse(referral.remarks));
        } else if (referral.remarks) {
          setActivityLog([{ type: 'note', text: referral.remarks, date: referral.updated_at || new Date().toISOString(), author: 'System' }]);
        } else {
          setActivityLog([]);
        }
      } catch (e) {
        setActivityLog([]);
      }
      setShowPayoutControls(false);
      setRewardAmount(3000);
      setUiError('');
      setShowEmailComposer(false);
      setEmailError('');
      setSendingLead(false);
    }
  }, [referral]);

  useEffect(() => {
    historyEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [activityLog]);

  const addToHistory = (text, type = 'system') => {
    const newEntry = { type, text, date: new Date().toISOString(), author: adminRole };
    setActivityLog(prev => [...prev, newEntry]);
  };

  const handleCheckboxChange = (key, checked) => {
    if (!isAdmin && checked === false && status[key] === true) {
      showUiError("Permission Denied: Only Admin can roll back completed steps.");
      return;
    }
    const stepName = progressSteps.find(s => s.key === key)?.label || key;
    setStatus(prev => ({ ...prev, [key]: checked }));

    if (checked) {
      setDates(prev => ({ ...prev, [`${key}_at`]: new Date().toISOString() }));
      addToHistory(`Marked "${stepName}" as complete.`, 'status');
    } else {
      setDates(prev => ({ ...prev, [`${key}_at`]: null }));
      addToHistory(`Rolled back "${stepName}".`, 'alert');
    }
  };

  const handleAddNote = () => {
    if (!newNote.trim()) return;
    addToHistory(newNote, 'note');
    setNewNote('');
  };

  // --- FIXED: Handle Final Status Change (Prevent Duplicate Logs) ---
  const handleFinalStatusChange = (clickedStatus) => {
    // 1. Permission Check
    if (!isAdmin && status.final_status !== 'Pending') {
      showUiError("Action Locked: Final Status is already set. Only Admin can change it.");
      return;
    }

    // 2. Check if we are toggling OFF (Reverting to Pending)
    const isTogglingOff = status.final_status === clickedStatus;

    if (isTogglingOff) {
      // --- LOGIC: REVERT TO PENDING ---
      addToHistory(`Unselected "${clickedStatus}". Reverted to Pending.`, 'alert');
      setStatus(prev => ({ ...prev, final_status: 'Pending' }));

      // Clear date if reverting admission
      if (clickedStatus === 'Admission Done' && isAdmin) {
        setDates(prev => ({ ...prev, admission_done_at: null }));
      }
    } else {
      // --- LOGIC: SET NEW STATUS ---
      addToHistory(`Changed final status to "${clickedStatus}".`, 'status');
      setStatus(prev => ({ ...prev, final_status: clickedStatus }));

      const now = new Date().toISOString();

      if (clickedStatus === 'Admission Done') {
        setDates(prev => ({ ...prev, admission_done_at: now }));
      }
      // Edge case: If switching FROM Admission Done TO Not Interested, clear the date
      else if (isAdmin && status.final_status === 'Admission Done') {
        setDates(prev => ({ ...prev, admission_done_at: null }));
      }
    }
  };

  // --- "Add Lead": one click, fixed template, no composer — notifies the leads inbox. ---
  const handleAddLead = async () => {
    setSendingLead(true);
    try {
      const token = localStorage.getItem('admin_token');
      const now = new Date().toISOString();
      const updatedLog = [
        ...activityLog,
        { type: 'success', text: 'Lead email sent to ethansleads@gmail.com.', date: now, author: adminRole }
      ];

      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/admin/referrals/${referral.id}/send-lead-email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ remarks: JSON.stringify(updatedLog) })
      });

      if (!response.ok) {
        const errData = await response.json();
        throw new Error(errData.message || 'Failed to send lead email.');
      }

      setActivityLog(updatedLog);
    } catch (error) {
      showUiError(error.message);
    } finally {
      setSendingLead(false);
    }
  };

  // --- "Not Applicable" is different from the other final statuses: picking it opens an
  // email composer, and the status only flips once the email actually sends. ---
  const handleNotApplicableClick = () => {
    if (!isAdmin && status.final_status !== 'Pending') {
      showUiError("Action Locked: Final Status is already set. Only Admin can change it.");
      return;
    }

    if (status.final_status === 'Not Applicable') {
      // Already active: toggle off like the other final-status buttons (no email involved).
      handleFinalStatusChange('Not Applicable');
      return;
    }

    if (!isAdmin) {
      showUiError("Only Admin can send the Not Applicable email.");
      return;
    }

    setEmailSubject(getNotApplicableSubject());
    setEmailBody(getDefaultNotApplicableBody(referral.referrer_name, referral.full_name, referral.mobile_no));
    setEmailError('');
    setShowEmailComposer(true);
  };

  const handleSendNotApplicableEmail = async () => {
    if (!emailSubject.trim() || !emailBody.trim()) {
      setEmailError('Subject and message cannot be empty.');
      return;
    }
    setSendingEmail(true);
    setEmailError('');
    try {
      const token = localStorage.getItem('admin_token');
      const now = new Date().toISOString();
      const updatedLog = [
        ...activityLog,
        { type: 'success', text: `Sent "Not Applicable" email to referrer (${referral.referrer_email}).`, date: now, author: adminRole }
      ];

      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/admin/referrals/${referral.id}/not-applicable-email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ subject: emailSubject, body: emailBody, remarks: JSON.stringify(updatedLog) })
      });

      if (!response.ok) {
        const errData = await response.json();
        throw new Error(errData.message || 'Failed to send email.');
      }

      setActivityLog(updatedLog);
      setStatus(prev => ({ ...prev, final_status: 'Not Applicable' }));
      setDates(prev => ({ ...prev, admission_done_at: null }));
      setShowEmailComposer(false);
      onUpdate();
    } catch (error) {
      setEmailError(error.message);
    } finally {
      setSendingEmail(false);
    }
  };

  const handleMarkCashSent = () => {
    if (!isAdmin) return;
    const now = new Date().toISOString();
    setStatus(prev => ({ ...prev, reward_sent: true, reward_status: 'Sent' }));
    setDates(prev => ({ ...prev, reward_sent_at: now }));
    addToHistory(`Payment Done: ₹${rewardAmount} paid via Cash/UPI.`, 'success');
  };

  const handleRollbackReward = () => {
    if (!isAdmin) return;
    if (window.confirm("Are you sure you want to rollback the reward payment?")) {
      setStatus(prev => ({ ...prev, reward_sent: false, reward_status: 'Pending' }));
      setDates(prev => ({ ...prev, reward_sent_at: null }));
      addToHistory(`Rolled back Reward Payment status.`, 'alert');
    }
  };

  const handleSaveChanges = async () => {
    setLoading(true);
    setUiError('');
    try {
      const token = localStorage.getItem('admin_token');
      let payload = { ...status };
      payload.remarks = JSON.stringify(activityLog);

      // --- SANITIZE PAYLOAD FOR COUNSELORS ---
      if (!isAdmin) {
        delete payload.reward_sent;
        delete payload.reward_status;
        delete payload.reward_type;
        delete payload.gift_card_link;
        delete payload.batch_type;
      } else {
        if (status.fees_paid && !status.reward_type) {
          payload.reward_type = 'Cash';
          payload.batch_type = 'Advance';
        }
      }

      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/admin/referrals/${referral.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        const errData = await response.json();
        throw new Error(errData.message || 'Failed to save changes.');
      }
      onUpdate();
      onClose();
    } catch (error) {
      showUiError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const getUpiQrValue = () => {
    const name = encodeURIComponent(referral.referrer_name);
    const upiId = referral.referrer_upi_id;
    const note = encodeURIComponent("Ethans Referral Reward");
    return `upi://pay?pa=${upiId}&pn=${name}&am=${rewardAmount}&cu=INR&tn=${note}`;
  };

  const leadEmailAlreadySent = activityLog.some(log => typeof log.text === 'string' && log.text.startsWith('Lead email sent'));

  const statusInfo = (() => {
    const { final_status, fees_paid, reward_status } = status;
    if (final_status === 'Not Interested') return { label: 'Not Interested', color: '#991B1B', bgColor: '#FEE2E2', icon: XCircle };
    if (final_status === 'Not Applicable') return { label: 'Not Applicable', color: '#374151', bgColor: '#F3F4F6', icon: Mail };
    if (fees_paid && reward_status === 'Sent') return { label: `Payment Done`, color: '#10B981', bgColor: '#D1FAE5', icon: IndianRupee };
    if (final_status === 'Admission Done') return { label: 'Admission Done', color: '#065F46', bgColor: '#D1FAE5', icon: CheckCircle2 };
    if (fees_paid) {
      return { label: 'Ready for Reward', color: '#667eea', bgColor: '#EEF2FF', icon: Sparkles };
    }
    return { label: 'In Progress', color: '#6B7280', bgColor: '#F3F4F6', icon: Clock };
  })();
  const StatusIcon = statusInfo.icon;

  const isLockedForCounselor = !isAdmin && status.final_status !== 'Pending';

  const TrackerStyles = () => (
    <style>{`
      @keyframes slideIn { from { transform: translateY(20px); opacity: 0; } to { transform: translateY(0); opacity: 1; } }
      @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
      .tracker-overlay { position: fixed; top: 0; left: 0; right: 0; bottom: 0; background: rgba(0,0,0,0.5); backdrop-filter: blur(5px); z-index: 1100; display: flex; align-items: center; justify-content: center; animation: fadeIn 0.3s ease; padding: 20px; }
      .tracker-modal { background: #FFFFFF; width: 100%; max-width: 900px; height: 85vh; max-height: 800px; border-radius: 16px; box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25); display: flex; flex-direction: column; overflow: hidden; animation: slideIn 0.4s cubic-bezier(0.16, 1, 0.3, 1); }
      .tracker-header { background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); padding: 24px 32px; color: white; position: relative; flex-shrink: 0; }
      .close-button { position: absolute; top: 20px; right: 20px; background: rgba(255,255,255,0.2); border: none; color: white; border-radius: 50%; width: 32px; height: 32px; cursor: pointer; display: flex; align-items: center; justify-content: center; transition: background 0.2s; }
      .close-button:hover { background: rgba(255,255,255,0.3); }
      .header-row { display: flex; justify-content: space-between; align-items: flex-start; margin-top: 5px; padding-right: 40px; }
      .tracker-title { margin: 0; font-size: 24px; font-weight: 700; }
      .tracker-meta { display: flex; gap: 12px; margin-top: 8px; }
      .meta-pill { display: flex; align-items: center; gap: 6px; font-size: 12px; background: rgba(0,0,0,0.2); padding: 4px 10px; border-radius: 20px; }
      .status-pill-large { display: flex; align-items: center; gap: 8px; padding: 8px 16px; border-radius: 30px; font-weight: 700; font-size: 14px; box-shadow: 0 4px 12px rgba(0,0,0,0.1); }
      .header-actions-row { margin-top: 14px; }
      .add-lead-btn { background: rgba(255,255,255,0.15); border: 1px solid rgba(255,255,255,0.4); color: white; padding: 9px 18px; border-radius: 10px; font-weight: 700; font-size: 13px; cursor: pointer; display: inline-flex; align-items: center; gap: 8px; backdrop-filter: blur(10px); transition: all 0.2s; }
      .add-lead-btn:hover:not(:disabled) { background: rgba(255,255,255,0.28); transform: translateY(-1px); }
      .add-lead-btn:disabled { opacity: 0.75; cursor: not-allowed; }
      .add-lead-btn.sent { background: rgba(16,185,129,0.25); border-color: rgba(16,185,129,0.6); opacity: 1; }
      .tracker-body { display: flex; flex: 1; overflow: hidden; background: #F9FAFB; }
      .tracker-left-panel { flex: 1.2; padding: 24px; overflow-y: auto; border-right: 1px solid #E5E7EB; }
      .tracker-right-panel { flex: 0.8; padding: 24px; background: #FFFFFF; border-left: 1px solid #E5E7EB; display: flex; flex-direction: column; }
      .section-header-text { font-size: 12px; text-transform: uppercase; letter-spacing: 1px; color: #9CA3AF; font-weight: 700; margin: 0 0 16px 0; display: flex; align-items: center; gap: 8px; }
      .progress-list { display: flex; flex-direction: column; gap: 10px; }
      .progress-row { display: flex; align-items: center; gap: 12px; padding: 12px 16px; background: white; border: 1px solid #E5E7EB; border-radius: 12px; cursor: pointer; transition: all 0.2s; }
      .progress-row:hover { border-color: var(--accent); transform: translateX(2px); }
      .progress-row.active { background: #fff; border-color: var(--accent); box-shadow: 0 2px 8px rgba(0,0,0,0.05); }
      .progress-icon-box { width: 32px; height: 32px; border-radius: 8px; background: #F3F4F6; color: #6B7280; display: flex; align-items: center; justify-content: center; }
      .progress-row.active .progress-icon-box { background: var(--accent); color: white; }
      .progress-text { flex: 1; display: flex; flex-direction: column; }
      .step-name { font-weight: 600; font-size: 14px; color: #1F2937; }
      .step-date { font-size: 11px; color: #6B7280; }
      .custom-checkbox { width: 18px; height: 18px; accent-color: var(--accent); }
      .admin-actions-container { margin-top: 32px; display: flex; flex-direction: column; gap: 20px; }
      .final-status-card { background: #F9FAFB; border-radius: 16px; padding: 16px; border: 1px solid #E5E7EB; }
      .final-status-header { display: flex; align-items: center; gap: 8px; font-size: 13px; font-weight: 800; color: #111827; margin-bottom: 12px; }
      .status-buttons-row { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; } 
      .status-btn { display: flex; flex-direction: row; align-items: center; justify-content: center; gap: 8px; padding: 14px; border-radius: 10px; border: 1px solid; background: white; cursor: pointer; transition: all 0.2s; }
      .status-btn span { font-size: 13px; font-weight: 600; }
      .btn-timestamp { font-size: 10px; font-weight: 500; margin-top: 2px; opacity: 0.9; }
      .status-btn.not-interested { border-color: #EF4444; color: #EF4444; }
      .status-btn.not-interested.active { background: #EF4444; color: white; }
      .status-btn.admission { border-color: #065F46; color: #065F46; }
      .status-btn.admission.active { background: #065F46; color: white; }
      .status-btn-full { display: flex; align-items: center; justify-content: center; gap: 8px; padding: 14px; border-radius: 10px; border: 1px dashed #6B7280; background: #F3F4F6; color: #374151; cursor: pointer; transition: all 0.2s; width: 100%; box-sizing: border-box; margin-top: 12px; font-weight: 600; font-size: 13px; }
      .status-btn-full span { font-size: 13px; font-weight: 600; }
      .status-btn-full.not-applicable.active { background: #374151; border-style: solid; border-color: #374151; color: white; }
      .email-composer-card { background: white; border: 2px solid #374151; border-radius: 12px; padding: 16px; margin-top: 12px; display: flex; flex-direction: column; gap: 4px; }
      .email-composer-header { display: flex; align-items: center; gap: 8px; color: #374151; margin-bottom: 4px; }
      .email-composer-header h3 { margin: 0; font-size: 15px; font-weight: 800; }
      .email-composer-hint { font-size: 12px; color: #6B7280; margin: 0 0 8px 0; }
      .email-field-label { font-size: 11px; font-weight: 700; color: #4B5563; text-transform: uppercase; letter-spacing: 0.5px; margin: 8px 0 6px 0; }
      .email-subject-input { width: 100%; padding: 10px 12px; border: 1px solid #E5E7EB; border-radius: 8px; font-size: 13px; box-sizing: border-box; }
      .email-subject-input:focus, .email-body-textarea:focus { outline: none; border-color: #374151; box-shadow: 0 0 0 3px rgba(55, 65, 81, 0.1); }
      .email-body-textarea { width: 100%; padding: 10px 12px; border: 1px solid #E5E7EB; border-radius: 8px; font-size: 13px; font-family: inherit; resize: vertical; box-sizing: border-box; line-height: 1.5; }
      .email-composer-actions { display: flex; align-items: center; justify-content: flex-end; gap: 12px; margin-top: 12px; }
      .reset-template-btn { background: white; border: 1px solid #D1D5DB; color: #374151; padding: 8px 14px; border-radius: 8px; font-size: 12px; font-weight: 600; cursor: pointer; display: flex; align-items: center; gap: 6px; }
      .reset-template-btn:hover { background: #F3F4F6; }
      .send-email-btn { background: #374151; color: white; border: none; padding: 10px 18px; border-radius: 8px; font-weight: 700; font-size: 13px; cursor: pointer; display: flex; align-items: center; gap: 8px; }
      .send-email-btn:hover { background: #1F2937; }
      .send-email-btn:disabled, .reset-template-btn:disabled, .cancel-link:disabled { opacity: 0.6; cursor: not-allowed; }
      .reward-payout-card { background: #EEF2FF; border: 2px solid #667eea; border-radius: 16px; padding: 20px; display: flex; flex-direction: column; gap: 12px; }
      .reward-card-header { display: flex; align-items: center; gap: 8px; color: #667eea; }
      .reward-card-header h3 { margin: 0; font-size: 16px; font-weight: 800; }
      .reward-status-line { font-size: 14px; color: #1F2937; }
      .status-text { font-weight: 700; margin-left: 4px; }
      .status-text.sent { color: #059669; }
      .status-text.pending { color: #667eea; }
      .sent-date { font-size: 13px; color: #6B7280; margin-left: 8px; }
      .rollback-btn { background: #DC2626; color: white; width: 100%; padding: 12px; border: none; border-radius: 10px; font-weight: 700; font-size: 14px; display: flex; align-items: center; justify-content: center; gap: 8px; cursor: pointer; transition: background 0.2s; margin-top: 8px; }
      .rollback-btn:hover { background: #B91C1C; }
      .show-payout-btn { background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); color: white; width: 100%; padding: 12px; border: none; border-radius: 10px; font-weight: 700; font-size: 14px; display: flex; align-items: center; justify-content: center; gap: 8px; cursor: pointer; transition: transform 0.2s; margin-top: 8px; }
      .show-payout-btn:hover { transform: translateY(-2px); box-shadow: 0 4px 12px rgba(102, 126, 234, 0.3); }
      .payout-form-expanded { background: white; border-radius: 12px; padding: 16px; margin-top: 8px; border: 1px solid #E5E7EB; }
      .amount-input-row { display: flex; align-items: center; justify-content: space-between; margin-bottom: 12px; font-size: 13px; font-weight: 600; }
      .amount-input-row input { width: 100px; padding: 6px; border-radius: 6px; border: 1px solid #E5E7EB; }
      .qr-compact { display: flex; gap: 12px; align-items: center; margin-bottom: 16px; }
      .upi-text { font-size: 12px; display: flex; flex-direction: column; }
      .no-upi-compact { display: flex; align-items: center; gap: 8px; font-size: 13px; font-weight: 600; padding: 12px; background: #F3F4F6; border-radius: 8px; margin-bottom: 12px; }
      .payout-actions { display: flex; gap: 10px; justify-content: flex-end; }
      .cancel-link { background: none; border: none; color: #6B7280; font-size: 13px; cursor: pointer; text-decoration: underline; }
      .confirm-sent-btn { background: #10B981; color: white; border: none; padding: 8px 16px; border-radius: 8px; font-weight: 700; cursor: pointer; }
      .history-feed { flex: 1; overflow-y: auto; padding-right: 4px; display: flex; flex-direction: column; gap: 16px; margin-bottom: 16px; }
      .empty-history { text-align: center; color: #9CA3AF; font-size: 13px; margin-top: 40px; }
      .history-item { display: flex; flex-direction: column; gap: 4px; align-self: flex-start; max-width: 90%; }
      .history-item.status { align-self: center; width: 100%; max-width: 100%; }
      .history-item.status .history-bubble { background: transparent; color: #6B7280; font-size: 12px; text-align: center; padding: 4px; border: none; font-style: italic; }
      .history-item.status .history-meta { display: none; }
      .history-item.note .history-bubble { background: #F3F4F6; color: #1F2937; border-bottom-left-radius: 2px; }
      .history-item.alert .history-bubble { background: #FEF2F2; color: #991B1B; border: 1px solid #FECACA; }
      .history-item.success .history-bubble { background: #ECFDF5; color: #065F46; border: 1px solid #6EE7B7; }
      .history-meta { font-size: 10px; color: #9CA3AF; display: flex; gap: 8px; margin-left: 4px; }
      .history-bubble { padding: 10px 14px; border-radius: 12px; font-size: 13px; line-height: 1.4; }
      .note-input-area { display: flex; gap: 8px; padding-top: 16px; border-top: 1px solid #E5E7EB; }
      .note-input-area input { flex: 1; padding: 10px 14px; border: 1px solid #E5E7EB; border-radius: 24px; outline: none; font-size: 13px; }
      .note-input-area input:focus { border-color: #667eea; }
      .note-input-area button { width: 36px; height: 36px; border-radius: 50%; border: none; background: #667eea; color: white; display: flex; align-items: center; justify-content: center; cursor: pointer; transition: background 0.2s; }
      .note-input-area button:hover { background: #5a6ed0; }
      .note-input-area button:disabled { background: #E5E7EB; cursor: not-allowed; }
      .tracker-footer { padding: 16px 32px; border-top: 1px solid #E5E7EB; display: flex; justify-content: flex-end; gap: 12px; background: #fff; flex-shrink: 0; }
      .btn-cancel { padding: 10px 20px; border: 1px solid #D1D5DB; background: white; border-radius: 8px; font-weight: 600; cursor: pointer; }
      .btn-save { padding: 10px 24px; background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); color: white; border: none; border-radius: 8px; font-weight: 600; cursor: pointer; }
      .inline-error-banner { background: #FEF2F2; color: #991B1B; border: 1px solid #FECACA; padding: 12px; border-radius: 8px; font-size: 13px; font-weight: 600; display: flex; align-items: center; gap: 8px; margin-bottom: 16px; animation: fadeIn 0.3s ease; }
      @media (max-width: 768px) {
        .tracker-modal { height: 100vh; border-radius: 0; }
        .tracker-body { flex-direction: column; }
        .tracker-left-panel { border-right: none; border-bottom: 1px solid #E5E7EB; }
        .tracker-right-panel { border-left: none; }
      }
    `}</style>
  );

  return (
    <>
      <TrackerStyles />
      <div className="tracker-overlay" onClick={onClose}>
        <div className="tracker-modal" onClick={e => e.stopPropagation()}>
          <div className="tracker-header">
            <button className="close-button" onClick={onClose}><X size={20} /></button>
            <div className="header-row">
              <div className="header-user-info">
                <h3 className="tracker-title">{referral.full_name}</h3>
                <div className="tracker-meta">
                  <span className="meta-pill"><UserCheck size={12} /> Referred by: {referral.referrer_name}</span>
                  <span className="meta-pill"><Calendar size={12} /> {formatDateTime(dates.created_at)}</span>
                </div>
              </div>
              <div className="status-pill-large" style={{ backgroundColor: statusInfo.bgColor, color: statusInfo.color }}>
                <StatusIcon size={20} />
                <span>{statusInfo.label}</span>
              </div>
            </div>
            <div className="header-actions-row">
              <button className={`add-lead-btn ${leadEmailAlreadySent ? 'sent' : ''}`} onClick={handleAddLead} disabled={sendingLead || leadEmailAlreadySent}>
                {sendingLead ? 'Sending...' : leadEmailAlreadySent ? (<><CheckCircle2 size={16} /> Lead Email Sent</>) : (<><UserPlus size={16} /> Add Lead</>)}
              </button>
            </div>
          </div>
          <div className="tracker-body">
            <div className="tracker-left-panel">

              {uiError && (
                <div className="inline-error-banner">
                  <AlertCircle size={18} />
                  <span>{uiError}</span>
                </div>
              )}

              <div className="panel-section">
                <h4 className="section-header-text">Journey Status</h4>
                <div className="progress-list">
                  {progressSteps.map(({ key, label, icon: StepIcon, color }) => {
                    const isCompleted = status[key];
                    return (
                      <label key={key} className={`progress-row ${isCompleted ? 'active' : ''}`} style={{ '--accent': color }}>
                        <div className="progress-icon-box"><StepIcon size={16} /></div>
                        <div className="progress-text">
                          <span className="step-name">{label}</span>
                          {dates[`${key}_at`] && <span className="step-date">{formatDateTime(dates[`${key}_at`])}</span>}
                        </div>
                        <input type="checkbox" className="custom-checkbox" checked={isCompleted} onChange={(e) => handleCheckboxChange(key, e.target.checked)} />
                      </label>
                    );
                  })}
                </div>
              </div>
              {(isAdmin || isCounselor) && (
                <div className="admin-actions-container">
                  <div className="final-status-card">
                    <div className="final-status-header"><Sparkles size={16} /><span>FINAL STATUS ACTIONS</span></div>
                    <div className="status-buttons-row">
                      <button
                        onClick={() => handleFinalStatusChange('Not Interested')}
                        disabled={isLockedForCounselor}
                        style={{
                          cursor: isLockedForCounselor ? 'not-allowed' : 'pointer',
                          opacity: (isLockedForCounselor && status.final_status !== 'Not Interested') ? 0.6 : 1
                        }}
                        className={`status-btn not-interested ${status.final_status === 'Not Interested' ? 'active' : ''}`}
                      >
                        <XCircle size={16} /><span>Not Interested</span>
                      </button>

                      <button
                        onClick={() => handleFinalStatusChange('Admission Done')}
                        disabled={isLockedForCounselor}
                        style={{
                          cursor: isLockedForCounselor ? 'not-allowed' : 'pointer',
                          opacity: (isLockedForCounselor && status.final_status !== 'Admission Done') ? 0.6 : 1
                        }}
                        className={`status-btn admission ${status.final_status === 'Admission Done' ? 'active' : ''}`}
                      >
                        <CheckCircle2 size={16} />
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', lineHeight: 1 }}>
                          <span>Admission Done</span>
                          {status.final_status === 'Admission Done' && dates.admission_done_at && (<span className="btn-timestamp">{formatDateTime(dates.admission_done_at)}</span>)}
                        </div>
                      </button>
                    </div>

                    <button
                      onClick={handleNotApplicableClick}
                      disabled={isLockedForCounselor}
                      style={{
                        cursor: isLockedForCounselor ? 'not-allowed' : 'pointer',
                        opacity: (isLockedForCounselor && status.final_status !== 'Not Applicable') ? 0.6 : 1
                      }}
                      className={`status-btn-full not-applicable ${status.final_status === 'Not Applicable' ? 'active' : ''}`}
                    >
                      <Mail size={16} />
                      <span>Not Applicable (Send Email)</span>
                      {status.final_status === 'Not Applicable' && <span className="btn-timestamp">Email Sent</span>}
                    </button>

                    {showEmailComposer && (
                      <div className="email-composer-card">
                        <div className="email-composer-header">
                          <Mail size={18} />
                          <h3>Send "Not Applicable" Email</h3>
                        </div>
                        <p className="email-composer-hint">
                          This will email the referrer, <strong>{referral.referrer_name}</strong> ({referral.referrer_email || 'no email on file'}), and mark the final status as <strong>Not Applicable</strong>.
                        </p>

                        {emailError && (
                          <div className="inline-error-banner">
                            <AlertCircle size={16} />
                            <span>{emailError}</span>
                          </div>
                        )}

                        <label className="email-field-label">Subject</label>
                        <input
                          type="text"
                          className="email-subject-input"
                          value={emailSubject}
                          onChange={(e) => setEmailSubject(e.target.value)}
                          disabled={sendingEmail}
                        />

                        <label className="email-field-label">Message</label>
                        <textarea
                          className="email-body-textarea"
                          rows={10}
                          value={emailBody}
                          onChange={(e) => setEmailBody(e.target.value)}
                          disabled={sendingEmail}
                        />

                        <div className="email-composer-actions">
                          <button
                            type="button"
                            className="cancel-link"
                            onClick={() => setShowEmailComposer(false)}
                            disabled={sendingEmail}
                          >
                            Cancel
                          </button>
                          <button
                            type="button"
                            className="reset-template-btn"
                            onClick={() => {
                              setEmailSubject(getNotApplicableSubject());
                              setEmailBody(getDefaultNotApplicableBody(referral.referrer_name, referral.full_name, referral.mobile_no));
                            }}
                            disabled={sendingEmail}
                          >
                            <RefreshCw size={14} /> Reset to Template
                          </button>
                          <button
                            type="button"
                            className="send-email-btn"
                            onClick={handleSendNotApplicableEmail}
                            disabled={sendingEmail || !referral.referrer_email}
                            title={!referral.referrer_email ? 'Referrer has no email address on file' : undefined}
                          >
                            {sendingEmail ? 'Sending...' : (<><Send size={16} /> Send Email</>)}
                          </button>
                        </div>
                      </div>
                    )}
                  </div>

                  {status.fees_paid && isAdmin && (
                    <div className="reward-payout-card">
                      <div className="reward-card-header"><IndianRupee size={20} /><h3>Reward Payout (Cash)</h3></div>
                      <div className="reward-status-line">
                        Status: <span className={`status-text ${status.reward_status === 'Sent' ? 'sent' : 'pending'}`}>{status.reward_status}</span>
                        {status.reward_status === 'Sent' && dates.reward_sent_at && (<span className="sent-date"> Sent: {formatDateTime(dates.reward_sent_at)}</span>)}
                      </div>
                      {status.reward_sent ? (
                        <button className="rollback-btn" onClick={handleRollbackReward}><RotateCcw size={18} /> Rollback Payment Done</button>
                      ) : (
                        <>
                          {!showPayoutControls ? (
                            <button className="show-payout-btn" onClick={() => setShowPayoutControls(true)}><Eye size={18} /> Show Payout Details</button>
                          ) : (
                            <div className="payout-form-expanded">
                              <div className="amount-input-row">
                                <label>Amount (₹)</label>
                                <input type="number" value={rewardAmount} onChange={e => setRewardAmount(e.target.value)} />
                              </div>
                              {referral.referrer_upi_id ? (
                                <div className="qr-compact">
                                  <QRCodeCanvas value={getUpiQrValue()} size={100} />
                                  <div className="upi-text"><small>Pay to:</small><strong>{referral.referrer_upi_id}</strong></div>
                                </div>
                              ) : (
                                <div className="no-upi-compact"><Phone size={16} /> <span>{referral.referrer_mobile_no || 'No Phone'}</span></div>
                              )}
                              <div className="payout-actions">
                                <button className="cancel-link" onClick={() => setShowPayoutControls(false)}>Cancel</button>
                                <button className="confirm-sent-btn" onClick={handleMarkCashSent}>Mark as Sent</button>
                              </div>
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  )}

                </div>
              )}
            </div>
            <div className="tracker-right-panel">
              <h4 className="section-header-text"><History size={16} /> Activity Log</h4>
              <div className="history-feed">
                {activityLog.length === 0 ? (<div className="empty-history">No activity recorded yet.</div>) : (
                  activityLog.map((log, idx) => (
                    <div key={idx} className={`history-item ${log.type}`}>
                      <div className="history-meta"><span className="history-author">{log.author}</span><span className="history-time">{formatDateTime(log.date)}</span></div>
                      <div className="history-bubble">{log.text}</div>
                    </div>
                  ))
                )}
                <div ref={historyEndRef} />
              </div>
              <div className="note-input-area">
                <input type="text" placeholder="Type a note..." value={newNote} onChange={(e) => setNewNote(e.target.value)} onKeyPress={(e) => e.key === 'Enter' && handleAddNote()} />
                <button onClick={handleAddNote} disabled={!newNote.trim()}><Send size={16} /></button>
              </div>
            </div>
          </div>
          <div className="tracker-footer">
            <button onClick={onClose} className="btn-cancel">Close</button>
            <button onClick={handleSaveChanges} disabled={loading} className="btn-save">{loading ? 'Saving...' : 'Save Changes'}</button>
          </div>
        </div>
      </div>
    </>
  );
};

// =================================================================================
// 3. MAIN REFERRALS PAGE COMPONENT
// =================================================================================

const ReferralsPage = () => {
  const [referrals, setReferrals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedReferral, setSelectedReferral] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [referrersList, setReferrersList] = useState([]);

  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const itemsPerPage = 10;

  const [filterType, setFilterType] = useState('month');
  const [selectedMonthYear, setSelectedMonthYear] = useState('');
  const [selectedDate, setSelectedDate] = useState('');

  // --- FILTERS STATE ---
  const [filters, setFilters] = useState({
    call_made: 'all',
    counselor_assigned: 'all',
    fees_paid: 'all',
    reward_sent: 'all',
    final_status: 'all',
    stage: 'all', // New Filter
    referred_by_id: 'all', // Added Referred By Filter
  });

  const { derivedStartDate, derivedEndDate } = useMemo(() => {
    if (filterType === 'month') {
      if (!selectedMonthYear) return { derivedStartDate: '', derivedEndDate: '' };
      const [year, month] = selectedMonthYear.split('-').map(Number);
      const start = new Date(year, month - 1, 1);
      const end = new Date(year, month, 0);
      return {
        derivedStartDate: toApiDate(start),
        derivedEndDate: toApiDate(end),
      };
    } else {
      if (!selectedDate) return { derivedStartDate: '', derivedEndDate: '' };
      return {
        derivedStartDate: selectedDate,
        derivedEndDate: selectedDate
      };
    }
  }, [filterType, selectedMonthYear, selectedDate]);

  const fetchReferrals = useCallback(async (isSilent = false) => {
    setError('');
    if (!isSilent) setLoading(true);

    try {
      const token = localStorage.getItem('admin_token');
      const params = new URLSearchParams();
      if (derivedStartDate) params.append('startDate', derivedStartDate);
      if (derivedEndDate) params.append('endDate', derivedEndDate);
      params.append('page', currentPage);
      params.append('limit', itemsPerPage);
      if (searchTerm) params.append('search', searchTerm);

      if (filters.call_made !== 'all') params.append('call_made', filters.call_made);
      if (filters.counselor_assigned !== 'all') params.append('counselor_assigned', filters.counselor_assigned);
      if (filters.fees_paid !== 'all') params.append('fees_paid', filters.fees_paid);
      if (filters.reward_sent !== 'all') params.append('reward_sent', filters.reward_sent);
      if (filters.final_status !== 'all') params.append('final_status', filters.final_status);
      if (filters.stage !== 'all') params.append('stage', filters.stage);
      if (filters.referred_by_id !== 'all') params.append('referred_by_id', filters.referred_by_id);

      const queryString = params.toString();

      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/admin/referrals${queryString ? `?${queryString}` : ''}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok) {
        const errData = await response.json();
        throw new Error(errData.message || 'Failed to fetch data.');
      }
      const data = await response.json();

      setReferrals(data.referrals || []);
      setTotalPages(data.totalPages || 1);
      setTotalItems(data.totalItems || 0);

    } catch (err) {
      setError(err.message);
    } finally {
      if (!isSilent) setLoading(false);
    }
  }, [derivedStartDate, derivedEndDate, currentPage, searchTerm, filters]);

  // Fetch list of referrers for the filter dropdown
  useEffect(() => {
    const fetchReferrers = async () => {
      try {
        const token = localStorage.getItem('admin_token');
        const response = await fetch(`${process.env.REACT_APP_API_URL}/api/admin/referrers-list`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (response.ok) {
          const data = await response.json();
          setReferrersList(data || []);
        }
      } catch (err) {
        console.error("Failed to fetch referrers list", err);
      }
    };
    fetchReferrers();
  }, []);

  useEffect(() => {
    const timerId = setTimeout(() => {
      fetchReferrals();
    }, 500);
    return () => clearTimeout(timerId);
  }, [fetchReferrals]);

  useEffect(() => {
    const intervalId = setInterval(() => {
      fetchReferrals(true);
    }, 5000);
    return () => clearInterval(intervalId);
  }, [fetchReferrals]);

  useEffect(() => {
    setCurrentPage(1);
  }, [derivedStartDate, derivedEndDate, searchTerm, filters]);

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
  };

  const handleOpenTracker = (referral) => {
    setSelectedReferral(referral);
    setIsModalOpen(true);
  };

  const handleCloseTracker = () => {
    setSelectedReferral(null);
    setIsModalOpen(false);
  };

  const getDisplayStatus = (ref) => {
    const finalStatus = ref.final_status || 'Pending';
    if (finalStatus === 'Not Interested') return { text: 'Not Interested', icon: XCircle, class: 'status-not-interested' };
    if (finalStatus === 'Not Applicable') return { text: 'Not Applicable', icon: Mail, class: 'status-not-applicable' };
    if (ref.reward_sent) return { text: 'Payment Done', icon: Gift, class: 'status-reward' };
    if (finalStatus === 'Admission Done') return { text: 'Admission Done', icon: CheckCircle2, class: 'status-admission-done' };
    if (ref.fees_paid) return { text: 'Fees Paid', icon: IndianRupee, class: 'status-fees' };
    if (ref.enrolled) return { text: 'Enrolled', icon: UserCheck, class: 'status-enrolled' };
    if (ref.counselor_assigned) return { text: 'Counselling Done', icon: UserCheck, class: 'status-counselor' };
    if (ref.call_made) return { text: 'Call Made', icon: Phone, class: 'status-call' };
    return { text: 'Pending', icon: Clock, class: 'status-pending' };
  };

  const FilterSelect = ({ name, label, icon: Icon }) => (
    <div className="filter-group-modern">
      <label htmlFor={name} className="filter-label-modern"><Icon size={14} />{label}</label>
      <select id={name} name={name} value={filters[name]} onChange={handleFilterChange} className="filter-select-modern">
        <option value="all">All</option>
        <option value="yes">Yes</option>
        <option value="no">No</option>
      </select>
    </div>
  );

  return (
    <div className="referrals-page-container">
      <PageStyles />
      <div className="referrals-header">
        <div className="header-content">
          <div className="header-badge">Referral Management</div>
          <h1 className="page-title">Manage <span className="gradient-text">All Referrals</span></h1>
        </div>
        <div className="header-filter-container">
          <div className="filter-row-top">
            <div className="search-container-modern">
              <Search size={18} className="search-icon-modern" />
              <input type="text" placeholder="Search by name, email, or referrer..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="search-input-modern" />
            </div>
            <div className="filter-group-modern date-group">
              <label className="filter-label-modern"><Calendar size={14} />Filter Period</label>
              <div className="date-input-row">
                <select className="filter-select-modern type-switcher" value={filterType} onChange={(e) => setFilterType(e.target.value)}>
                  <option value="month">Month</option>
                  <option value="day">Day</option>
                </select>
                {filterType === 'month' ? (
                  <input type="month" value={selectedMonthYear} onChange={(e) => setSelectedMonthYear(e.target.value)} className="filter-select-modern date-input" />
                ) : (
                  <input type="date" value={selectedDate} onChange={(e) => setSelectedDate(e.target.value)} className="filter-select-modern date-input" />
                )}
              </div>
            </div>
            <div className="filter-group-modern referred-by-group">
              <label htmlFor="referred_by_id" className="filter-label-modern"><UserCheck size={14} />Referred By</label>
              <select id="referred_by_id" name="referred_by_id" value={filters.referred_by_id} onChange={handleFilterChange} className="filter-select-modern referred-by-select">
                <option value="all">All Referrers</option>
                {referrersList.map(ref => (
                  <option key={ref.id} value={ref.id}>
                    {ref.full_name} ({ref.email})
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div className="filter-row-bottom">
            <FilterSelect name="call_made" label="Call Made" icon={Phone} />
            <FilterSelect name="counselor_assigned" label="Counselling Done" icon={UserCheck} />
            <FilterSelect name="fees_paid" label="Fees Paid" icon={IndianRupee} />
            <FilterSelect name="reward_sent" label="Payment Done" icon={Gift} />
            <div className="filter-group-modern">
              <label htmlFor="stage" className="filter-label-modern"><TrendingUp size={14} />Current Stage</label>
              <select id="stage" name="stage" value={filters.stage} onChange={handleFilterChange} className="filter-select-modern">
                <option value="all">All Stages</option>
                <option value="new">New (No Action)</option>
                <option value="call_made">Call Made Only</option>
                <option value="counseling">Counselling Only</option>
                <option value="enrolled">Enrolled Only</option>
                <option value="fees_paid">Fees Paid (Reward Pending)</option>
                <option value="reward_sent">Payment Done</option>
              </select>
            </div>
            <div className="filter-group-modern">
              <label htmlFor="final_status" className="filter-label-modern"><CheckCircle2 size={14} />Admission Status</label>
              <select id="final_status" name="final_status" value={filters.final_status} onChange={handleFilterChange} className="filter-select-modern">
                <option value="all">All</option>
                <option value="Pending">Pending</option>
                <option value="Admission Done">Admission Done</option>
                <option value="Not Interested">Not Interested</option>
                <option value="Not Applicable">Not Applicable</option>
              </select>
            </div>
          </div>
        </div>
      </div>
      {error && (
        <div className="error-banner"><AlertCircle size={20} /><strong>Error:</strong> {error}</div>
      )}
      <div className="table-card-modern">
        {loading ? (
          <div className="table-loader"><Spinner /><p className="loader-text">Loading referrals...</p></div>
        ) : (
          <div className="table-wrapper">
            <table className="modern-table">
              <thead>
                <tr>
                  <th>Referral</th><th>Contact Info</th><th>Referred By</th><th>Status</th><th>Registration Date</th><th>Last Update</th><th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {referrals.map(ref => {
                  const status = getDisplayStatus(ref);
                  const StatusIcon = status.icon;
                  return (
                    <tr key={ref.id}>
                      <td><div className="user-cell"><Avatar name={ref.full_name} /><div className="user-info"><span className="user-name">{ref.full_name}</span></div></div></td>
                      <td><div className="contact-cell"><span className="contact-email" title={ref.email}>{ref.email}</span><span className="contact-phone" title={ref.mobile_no}>{ref.mobile_no}</span></div></td>
                      <td><span className="referrer-name" title={ref.referrer_name}>{ref.referrer_name}</span></td>
                      <td><div className={`status-badge-modern ${status.class}`}><StatusIcon size={14} /><span>{status.text}</span></div></td>
                      <td><span className="date-text">{formatDateTime(ref.created_at)}</span></td>
                      <td><span className="date-text">{formatDateTime(ref.updated_at)}</span></td>
                      <td><button onClick={() => handleOpenTracker(ref)} className="track-button-modern"><TrendingUp size={16} />Track</button></td>
                    </tr>
                  );
                })}
                {referrals.length === 0 && (
                  <tr><td colSpan="7" className="no-data-cell"><div className="no-data-message"><AlertCircle size={48} className="no-data-icon" /><p className="no-data-text">No referrals found</p><p className="no-data-subtext">{searchTerm ? 'Try adjusting your search or filter criteria' : 'No referrals match the selected period'}</p></div></td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}
        <div className="pagination-footer">
          <div className="pagination-info">Showing {(currentPage - 1) * itemsPerPage + 1} to {Math.min(currentPage * itemsPerPage, totalItems)} of {totalItems} results</div>
          <div className="pagination-buttons">
            <button className="page-btn" disabled={currentPage === 1 || loading} onClick={() => handlePageChange(currentPage - 1)}><ChevronLeft size={16} /> Previous</button>
            <span className="page-indicator">Page {currentPage} of {totalPages}</span>
            <button className="page-btn" disabled={currentPage === totalPages || loading} onClick={() => handlePageChange(currentPage + 1)}>Next <ChevronRight size={16} /></button>
          </div>
        </div>
      </div>
      {isModalOpen && selectedReferral && (<ReferralTracker referral={selectedReferral} onClose={handleCloseTracker} onUpdate={fetchReferrals} />)}
    </div >
  );
};

const PageStyles = () => (
  <style>{`
    @keyframes fadeInUp { from { opacity: 0; transform: translateY(30px); } to { opacity: 1; transform: translateY(0); } }
    @keyframes spin { to { transform: rotate(360deg); } }
    @keyframes gradientFlow { 0% { background-position: 0% 50%; } 50% { background-position: 100% 50%; } 100% { background-position: 0% 50%; } }
    .referrals-page-container { animation: fadeInUp 0.6s ease-out; padding: 0; font-family: 'Inter', sans-serif; }
    .spinner { animation: spin 1s linear infinite; width: 2.25rem; height: 2.25rem; color: #667eea; }
    .spinner .path { stroke: currentColor; stroke-linecap: round; }
    .referrals-header { background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); border-radius: 24px; padding: 32px 40px; margin-bottom: 32px; box-shadow: 0 20px 60px rgba(102, 126, 234, 0.3); position: relative; overflow: visible; animation: fadeInUp 0.6s ease-out; }
    .referrals-header::before { content: ''; position: absolute; top: -50%; right: -50%; width: 200%; height: 200%; background: radial-gradient(circle, rgba(255, 255, 255, 0.1) 0%, transparent 70%); animation: spin 20s linear infinite; pointer-events: none; }
    .header-content { position: relative; z-index: 1; margin-bottom: 24px; }
    .header-badge { display: inline-block; background: rgba(255, 255, 255, 0.2); color: #ffffff; padding: 8px 20px; border-radius: 50px; font-size: 12px; font-weight: 700; text-transform: uppercase; letter-spacing: 2px; margin-bottom: 16px; backdrop-filter: blur(10px); }
    .page-title { margin: 0; font-size: clamp(2rem, 5vw, 3rem); font-weight: 900; color: #FFFFFF; letter-spacing: -0.025em; line-height: 1.2; margin-bottom: 0; }
    .gradient-text { background: linear-gradient(135deg, #FFD700 0%, #FFA500 100%); -webkit-background-clip: text; -webkit-text-fill-color: transparent; display: inline-block; }
    .error-banner { background: linear-gradient(135deg, #fef2f2 0%, #fee2e2 100%); color: #991b1b; border: 2px solid #fecaca; padding: 1.25rem 1.5rem; border-radius: 16px; margin-bottom: 2rem; font-weight: 600; box-shadow: 0 4px 15px rgba(239, 68, 68, 0.1); animation: fadeInUp 0.4s ease-out; display: flex; align-items: center; gap: 12px; }
    .header-filter-container { position: relative; z-index: 1; background: rgba(0,0,0,0.15); backdrop-filter: blur(5px); border: 1px solid rgba(255,255,255,0.1); border-radius: 16px; padding: 24px; display: flex; flex-direction: column; gap: 20px; animation: fadeInUp 0.6s ease-out 0.2s forwards; opacity: 0; }
    .filter-row-top { display: flex; align-items: flex-end; gap: 12px; flex-wrap: wrap; width: 100%; }
    .filter-row-bottom { display: flex; align-items: flex-end; gap: 12px; flex-wrap: wrap; width: 100%; }
    .search-container-modern { position: relative; flex: 1; min-width: 250px; }
    .referred-by-group { min-width: 200px; flex: 1; max-width: 300px; }
    .referred-by-select { width: 100%; }
    .search-icon-modern { position: absolute; left: 14px; top: 50%; transform: translateY(-50%); color: rgba(255,255,255,0.6); pointer-events: none; }
    .search-input-modern { width: 100%; padding: 12px 14px 12px 44px; border-radius: 12px; border: 2px solid rgba(255, 255, 255, 0.2); background: rgba(255, 255, 255, 0.1); font-size: 14px; font-weight: 500; color: #FFFFFF; transition: all 0.3s ease; box-sizing: border-box; }
    .search-input-modern::placeholder { color: rgba(255,255,255,0.6); }
    .search-input-modern:focus { outline: none; border-color: #FFFFFF; background: rgba(255, 255, 255, 0.2); box-shadow: 0 0 0 4px rgba(255, 255, 255, 0.1); }
    .filter-group-modern { display: flex; flex-direction: column; gap: 6px; }
    .filter-label-modern { font-size: 12px; font-weight: 700; color: rgba(255, 255, 255, 0.8); text-transform: uppercase; letter-spacing: 0.5px; display: flex; align-items: center; gap: 6px; }
    .filter-select-modern { padding: 11px 14px; border-radius: 10px; border: 2px solid rgba(255, 255, 255, 0.2); background: rgba(255, 255, 255, 0.1); font-size: 14px; font-weight: 600; color: #FFFFFF; cursor: pointer; transition: all 0.3s ease; min-width: 130px; -webkit-appearance: none; -moz-appearance: none; appearance: none; background-image: url('data:image/svg+xml;charset=US-ASCII,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%2224%22%20height%3D%2224%22%20viewBox%3D%220%200%2024%2024%22%20fill%3D%22none%22%20stroke%3D%22rgba(255,255,255,0.6)%22%20stroke-width%3D%222%22%20stroke-linecap%3D%22round%22%20stroke-linejoin%3D%22round%22%3E%3Cpolyline%20points%3D%226%209%2012%2015%2018%209%22%3E%3C%2Fpolyline%3E%3C%2Fsvg%3E'); background-repeat: no-repeat; background-position: right 14px center; background-size: 16px; padding-right: 40px; }
    .filter-select-modern option { color: #111827; }
    .filter-select-modern:focus { outline: none; border-color: #FFFFFF; background: rgba(255, 255, 255, 0.2); box-shadow: 0 0 0 4px rgba(255, 255, 255, 0.1); }
    .date-input-row { display: flex; gap: 8px; }
    .type-switcher { min-width: 90px; }
    .date-input { min-width: 160px; padding-right: 14px; background-image: none; }
    .table-card-modern { background: #FFFFFF; border-radius: 20px; border: 1px solid #E5E7EB; box-shadow: 0 4px 20px rgba(0, 0, 0, 0.04); overflow: hidden; animation: fadeInUp 0.6s ease-out 0.4s forwards; opacity: 0; display: flex; flex-direction: column; }
    .table-loader { display: flex; flex-direction: column; align-items: center; justify-content: center; padding: 80px 20px; gap: 20px; flex: 1; }
    .loader-text { font-size: 1.125rem; font-weight: 600; color: #6B7280; margin: 0; }
    .table-wrapper { flex: 1; overflow-x: auto; }
    .modern-table { width: 100%; border-collapse: collapse; table-layout: fixed; }
    .modern-table thead { background: linear-gradient(135deg, #F9FAFB 0%, #F3F4F6 100%); }
    .modern-table th { padding: 20px 24px; text-align: left; font-size: 12px; font-weight: 800; color: #6B7280; text-transform: uppercase; letter-spacing: 1px; border-bottom: 2px solid #E5E7EB; white-space: nowrap; }
    .modern-table tbody tr { border-bottom: 1px solid #F3F4F6; transition: background-color 0.2s ease; }
    .modern-table tbody tr:hover { background: #F9FAFB; }
    .modern-table td { padding: 20px 24px; vertical-align: middle; font-size: 14px; color: #1F2937; overflow: hidden; text-overflow: ellipsis; }
    .user-cell { display: flex; align-items: center; gap: 14px; }
    .avatar-circle { width: 48px; height: 48px; border-radius: 50%; display: flex; align-items: center; justify-content: center; font-weight: 700; font-size: 1.125rem; flex-shrink: 0; box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1); border: 3px solid #ffffff; }
    .user-info { display: flex; flex-direction: column; gap: 2px; }
    .user-name { font-weight: 700; font-size: 15px; color: #111827; white-space: nowrap; }
    .contact-cell { display: flex; flex-direction: column; gap: 4px; }
    .contact-email { font-size: 14px; color: #1F2937; font-weight: 500; overflow: hidden; text-overflow: ellipsis; }
    .contact-phone { font-size: 13px; color: #6B7280; font-weight: 500; }
    .referrer-name { font-weight: 600; color: #4B5563; overflow: hidden; text-overflow: ellipsis; }
    .status-badge-modern { display: inline-flex; align-items: center; gap: 6px; padding: 8px 14px; border-radius: 20px; font-size: 13px; font-weight: 700; white-space: nowrap; }
    .status-pending { background: #F3F4F6; color: #6B7280; }
    .status-call { background: #FEF3C7; color: #92400E; }
    .status-counselor { background: #EDE9FE; color: #5B21B6; }
    .status-enrolled { background: #DBEAFE; color: #1E40AF; }
    .status-fees { background: #D1FAE5; color: #065F46; }
    .status-reward { background: #FCE7F3; color: #9F1239; }
    .status-admission-done { background: linear-gradient(135deg, #D1FAE5 0%, #A7F3D0 100%); color: #065F46; font-weight: 800; }
    .status-not-interested { background: linear-gradient(135deg, #FEE2E2 0%, #FECACA 100%); color: #991B1B; font-weight: 800; }
    .status-not-applicable { background: #E5E7EB; color: #374151; font-weight: 800; }
    .date-text { font-size: 13px; color: #6B7280; font-weight: 500; white-space: nowrap; }
    .track-button-modern { background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); color: #FFFFFF; border: none; border-radius: 10px; padding: 10px 20px; font-size: 14px; font-weight: 700; cursor: pointer; display: inline-flex; align-items: center; gap: 8px; transition: all 0.3s ease; box-shadow: 0 4px 15px rgba(102, 126, 234, 0.3); }
    .track-button-modern:hover { transform: translateY(-2px); box-shadow: 0 8px 25px rgba(102, 126, 234, 0.4); }
    .no-data-cell { text-align: center !important; padding: 0 !important; height: 200px; vertical-align: middle !important; }
    .no-data-message { display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 16px; height: 100%; }
    .no-data-icon { color: #D1D5DB; }
    .no-data-text { color: #6B7280; font-size: 1.25rem; font-weight: 700; margin: 0; }
    .no-data-subtext { color: #9CA3AF; font-size: 0.875rem; margin: 0; }
    .pagination-footer { padding: 16px 24px; border-top: 1px solid #E5E7EB; display: flex; align-items: center; justify-content: space-between; background: #F9FAFB; }
    .pagination-info { font-size: 13px; color: #6B7280; font-weight: 500; }
    .pagination-buttons { display: flex; align-items: center; gap: 12px; }
    .page-btn { display: flex; align-items: center; gap: 6px; padding: 8px 16px; border-radius: 8px; border: 1px solid #E5E7EB; background: #FFFFFF; color: #374151; font-size: 13px; font-weight: 600; cursor: pointer; transition: all 0.2s; }
    .page-btn:hover:not(:disabled) { background: #F3F4F6; border-color: #D1D5DB; }
    .page-btn:disabled { opacity: 0.5; cursor: not-allowed; }
    @media (max-width: 1024px) { .header-filter-container { flex-direction: column; align-items: stretch; } .filter-section-left, .filter-section-right { width: 100%; } .filter-section-right { display: grid; grid-template-columns: 1fr 1fr; } }
    @media (max-width: 768px) { .referrals-header { padding: 32px 24px; } .page-title { font-size: 1.875rem; } .search-container-modern { max-width: 100%; } .filter-section-right { display: grid; grid-template-columns: 1fr 1fr; width: 100%; } .filter-select-modern { min-width: 0; } .modern-table th, .modern-table td { padding: 16px; font-size: 13px; } .avatar-circle { width: 40px; height: 40px; font-size: 1rem; } .pagination-footer { flex-direction: column; gap: 12px; } }
    @media (max-width: 540px) { .filter-section-right { grid-template-columns: 1fr; } .date-input-row { flex-direction: column; } .type-switcher, .date-input { width: 100%; } }
  `}</style>
);

export default ReferralsPage;